import * as React from 'react'
import { Link, useNavigate } from 'react-router'
import { Building2, Plus, Search } from 'lucide-react'
import type { ClientDto } from '@lib/dto/client.dto'
import { CLIENT_STATUSES, CLIENT_STATUS_LABELS } from '@lib/enums/client-status.enum'
import { CLIENT_TYPES, CLIENT_TYPE_LABELS } from '@lib/enums/client-type.enum'
import type { ClientStatus } from '@lib/enums/client-status.enum'
import type { ClientType } from '@lib/enums/client-type.enum'
import { useClients } from '@/hooks/useClients'
import { useDebounced } from '@/hooks/useDebounced'
import { ClientStatusBadge, ClientTypeBadge } from '@/components/ClientBadges'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

/** One-line summary of a client's city/state, or a dash when unknown. */
function locationLabel(client: ClientDto): string {
  const parts = [client.city, client.state].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : '—'
}

interface FilterTabsProps<T extends string> {
  value: T | 'ALL'
  options: readonly T[]
  labels: Record<T, string>
  onChange: (value: T | 'ALL') => void
}

/**
 * Segmented filter. Chrome stays neutral and only the selected tab carries a
 * surface, per the design system's "color only where it means something".
 */
function FilterTabs<T extends string>({
  value,
  options,
  labels,
  onChange,
}: FilterTabsProps<T>) {
  const all: (T | 'ALL')[] = ['ALL', ...options]
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-default bg-app p-0.5">
      {all.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={cn(
            'rounded px-2 py-1 text-xs font-medium transition-colors',
            option === value
              ? 'bg-surface text-primary-text shadow-sm'
              : 'text-muted-text hover:text-primary-text',
          )}
        >
          {option === 'ALL' ? 'All' : labels[option]}
        </button>
      ))}
    </div>
  )
}

/**
 * The client list — the app's primary surface. A dense, scannable table rather
 * than a card grid: this is the screen someone keeps open all day, and rows per
 * screen matter more than decoration.
 */
export function ClientsPage() {
  const navigate = useNavigate()

  const [search, setSearch] = React.useState<string>('')
  const [type, setType] = React.useState<ClientType | 'ALL'>('ALL')
  const [status, setStatus] = React.useState<ClientStatus | 'ALL'>('ALL')
  const [includeArchived, setIncludeArchived] = React.useState<boolean>(false)

  const debouncedSearch = useDebounced(search, 250)

  const clients = useClients({
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(type !== 'ALL' ? { type } : {}),
    ...(status !== 'ALL' ? { status } : {}),
    includeArchived,
  })

  const hasFilters =
    debouncedSearch !== '' || type !== 'ALL' || status !== 'ALL' || includeArchived

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Clients</h1>
          <p className="mt-1 text-sm text-muted-text">
            Commercial and residential clients, their people, and their sites.
          </p>
        </div>
        <Button onClick={() => navigate('/clients/new')}>
          <Plus className="h-4 w-4" />
          New client
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-text" />
          <Input
            className="pl-8"
            placeholder="Search clients"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <FilterTabs
          value={type}
          options={CLIENT_TYPES}
          labels={CLIENT_TYPE_LABELS}
          onChange={setType}
        />
        <FilterTabs
          value={status}
          options={CLIENT_STATUSES}
          labels={CLIENT_STATUS_LABELS}
          onChange={setStatus}
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-text">
          <input
            type="checkbox"
            className="rounded border-default"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Show archived
        </label>
      </div>

      <ClientsTable
        isPending={clients.isPending}
        isError={clients.isError}
        clients={clients.data?.clients ?? []}
        hasFilters={hasFilters}
      />

      {clients.data ? (
        <p className="text-xs text-muted-text">
          {clients.data.total} client{clients.data.total === 1 ? '' : 's'}
        </p>
      ) : null}
    </div>
  )
}

interface ClientsTableProps {
  isPending: boolean
  isError: boolean
  clients: ClientDto[]
  hasFilters: boolean
}

function ClientsTable({
  isPending,
  isError,
  clients,
  hasFilters,
}: ClientsTableProps) {
  if (isPending) {
    return <TableSkeleton />
  }

  if (isError) {
    return (
      <EmptyState title="Could not load clients">
        Refresh the page to try again.
      </EmptyState>
    )
  }

  if (clients.length === 0) {
    return hasFilters ? (
      <EmptyState title="No clients match those filters">
        Try a different search or clear the filters.
      </EmptyState>
    ) : (
      <EmptyState title="No clients yet">
        Add your first client to start building proposals.
      </EmptyState>
    )
  }

  return (
    <div className={TABLE_FRAME}>
      <table className={TABLE}>
        <thead>
          <tr className="border-b border-default text-secondary-text">
            <th className={cn(TH, COL_STICKY, 'bg-surface')}>Name</th>
            <th className={TH}>Status</th>
            <th className={cn(TH, COL_SECONDARY)}>Type</th>
            <th className={cn(TH, COL_SECONDARY)}>Location</th>
            <th className={cn(TH_RIGHT, COL_OPTIONAL)}>Contacts</th>
            <th className={cn(TH_RIGHT, COL_OPTIONAL)}>Sites</th>
            <th className={cn(TH, COL_OPTIONAL)}>Owner</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr
              key={client.id}
              className={cn(
                ROW,
                'border-b border-default last:border-b-0',
                client.archivedAt ? 'opacity-55' : null,
              )}
            >
              <td className={cn(TD, COL_STICKY)}>
                <Link
                  to={`/clients/${client.id}`}
                  className="font-medium hover:text-brand hover:underline"
                >
                  {client.name}
                </Link>
                {client.archivedAt ? (
                  <span className="ml-2 text-xs text-muted-text">Archived</span>
                ) : null}
              </td>
              <td className={TD}>
                <ClientStatusBadge status={client.status} />
              </td>
              <td className={cn(TD, COL_SECONDARY)}>
                <ClientTypeBadge type={client.type} />
              </td>
              <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
                {locationLabel(client)}
              </td>
              <td
                className={cn(
                  TD_RIGHT,
                  COL_OPTIONAL,
                  'tabular-nums text-secondary-text',
                )}
              >
                {client.contactCount}
              </td>
              <td
                className={cn(
                  TD_RIGHT,
                  COL_OPTIONAL,
                  'tabular-nums text-secondary-text',
                )}
              >
                {client.siteCount}
              </td>
              <td className={cn(TD, COL_OPTIONAL, 'text-muted-text')}>
                {client.ownerName ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="rounded-lg border border-default bg-surface">
      {[0, 1, 2, 3, 4].map((row) => (
        <div
          key={row}
          className="flex items-center gap-4 border-b border-default px-3 py-2 last:border-b-0"
        >
          {[0, 1, 2, 3].map((column) => (
            <div
              key={column}
              className="h-3.5 flex-1 animate-pulse rounded bg-subtle"
            />
          ))}
        </div>
      ))}
    </div>
  )
}

interface EmptyStateProps {
  title: string
  children: React.ReactNode
}

function EmptyState({ title, children }: EmptyStateProps) {
  return (
    <div className="rounded-lg border border-default bg-surface px-3 py-12 text-center">
      <Building2 className="mx-auto h-6 w-6 text-disabled-text" />
      <p className="mt-3 text-sm font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-text">{children}</p>
    </div>
  )
}
